/**
 * Acesso centralizado às variáveis de ambiente.
 * Único lugar do app que lê process.env.
 */
/**
 * Endereço da nest usado pelo SERVIDOR. `API_URL_INTERNAL` (runtime, sem
 * NEXT_PUBLIC) aponta para o Service do cluster: sem DNS externo e sem a volta
 * pelo load balancer público + Kong. Sem ela, vale o `NEXT_PUBLIC_API_URL`
 * (assado no build), o endereço público.
 *
 * O browser nunca usa nenhum dos dois: fala só com o proxy same-origin
 * `/api/nest` (ver `private-http.ts` e `public-http.ts`).
 */
const internalApiUrl = process.env.API_URL_INTERNAL?.trim() || undefined;

export const env = {
    /** URL base da nest-end-customer-api (server-side; ver `internalApiUrl`). */
    apiUrl: internalApiUrl ?? process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001",

    /** Variável de onde o `apiUrl` saiu — o relatório do boot manda conferir ESTA. */
    apiUrlSource: internalApiUrl ? "API_URL_INTERNAL" : "NEXT_PUBLIC_API_URL",

    // O tenant (x-client-suffix) NÃO mora aqui: ele não é config de build nem
    // de deploy, é dado da requisição. Ver @/config/tenant.

    /** Host que serve as mídias do portal (uploads, assets, banners). */
    mediaUrl: process.env.NEXT_PUBLIC_MEDIA_URL ?? "https://cdn-media.big2be.com",

    /**
     * Host que serve as imagens de produto — bucket/CDN separado do de mídia do
     * portal, com ciclo de vida próprio (vem da integração de catálogo, não de
     * upload no portal). Resolvido por `resolveProductImageUrl`.
     */
    productsMediaUrl: process.env.NEXT_PUBLIC_PRODUCTS_MEDIA_URL ?? "https://cdn-ecommerce.big2be.com",

    /** API de consulta de CEP (auto-preenchimento de endereço). */
    cepApiUrl: process.env.NEXT_PUBLIC_CEP_API_URL ?? "https://cep.big2be.com",

    /**
     * Backend do chat (chat-ecommerce-v4), canal `ecommerce`. SERVER-ONLY (sem
     * NEXT_PUBLIC): o browser nunca fala com ele — só o BFF /api/chat, que
     * carimba suffix, sessão e o JWT do cliente. `internalKey` deve bater com
     * ECOMMERCE_CHANNEL_INTERNAL_KEY no chat.
     */
    chat: {
        apiUrl: process.env.CHAT_API_URL ?? "http://localhost:3020",
        internalKey: process.env.CHAT_API_INTERNAL_KEY ?? "",
    },

    /**
     * Habilita a rota /checkout/link-pagamento. O scaffold ainda não tem a
     * integração real de geração de link — desligado (404) por padrão para
     * não expor uma tela que "gera" link sem gerar nada.
     */
    linkPaymentEnabled: process.env.NEXT_PUBLIC_LINK_PAYMENT_ENABLED === "true",

    /** Config do app web no Firebase (Console > Configurações do projeto > Seus apps). */
    firebase: {
        apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY ?? "",
        authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ?? "",
        projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ?? "",
        storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET ?? "",
        messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ?? "",
        appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID ?? "",
        measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID ?? "",
    },

    /** Chave pública do Web Push (Console > Cloud Messaging > Web Push certificates). */
    firebaseVapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY ?? "",
} as const;
